<template>
    <pendo-modal
        type="confirmation"
        :visible="visible"
        :title="$t(verificationModalTitleI18nKey)"
        :confirm-button-config="confirmButtonConfig"
        @confirm="onConfirm"
        @close="onCancel"
        @closed="onClosed"
        @cancel="onCancel">
        <template #body>
            <div v-if="['verify', 'accept'].includes(action)">
                <i18n-t
                    :keypath="verificationModalBodyI18nKey"
                    tag="p">
                    <template #verified>
                        <strong>{{ $t('Verified') }}</strong>
                    </template>
                </i18n-t>
            </div>
            <div v-if="action === 'request'">
                <i18n-t
                    :keypath="verificationModalBodyI18nKey"
                    tag="p">
                    <template #entityName>
                        <strong>{{ entityName }}</strong>
                    </template>
                </i18n-t>
                <div v-if="allowNote">
                    <strong class="note-label">{{ $t('verificationModal.Note') }}</strong>
                    <textarea-input
                        v-model="note"
                        :placeholder="$t('verificationModal.Add a note for context')" />
                </div>
            </div>
            <div v-if="action === 'rerequest'">
                <p>
                    {{ verificationModalBodyI18nKey }}
                </p>
                <div v-if="allowNote">
                    <strong class="note-label">Note</strong>
                    <textarea-input
                        v-model="note"
                        placeholder="Add a note for context" />
                </div>
            </div>
            <div v-if="action === 'reject'">
                <i18n-t
                    :keypath="verificationModalBodyI18nKey"
                    tag="p">
                    <template #rejected>
                        <strong>{{ $t('Rejected') }}</strong>
                    </template>
                </i18n-t>
            </div>
            <div v-if="action === 'cancel'">
                <i18n-t
                    :keypath="verificationModalBodyI18nKey"
                    tag="p">
                    <template #unverified>
                        <strong>{{ $t('Unverified') }}</strong>
                    </template>
                </i18n-t>
            </div>
            <div v-if="action === 'unverify'">
                <i18n-t
                    :keypath="verificationModalBodyI18nKey"
                    tag="p">
                    <template #entityName>
                        <strong>{{ entityName }}</strong>
                    </template>
                    <template #updatedBy>
                        {{ updatedBy }}
                    </template>
                    <template #updatedAt>
                        {{ updatedAtString }}
                    </template>
                    <template #unverified>
                        <strong>{{ $t('Unverified') }}</strong>
                    </template>
                    <template #br>
                        <br><br>
                    </template>
                </i18n-t>
            </div>
        </template>
    </pendo-modal>
</template>

<script>
import { PendoModal } from '@pendo/components';
import { TextareaInput } from '@pendo/tagging';
import { i18n_locale } from '@/utils/i18n';

export default {
    name: 'VerificationModal',
    components: {
        PendoModal,
        TextareaInput,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        action: {
            type: String,
            required: true,
            validator: (value) =>
                ['verify', 'unverify', 'request', 'rerequest', 'reject', 'cancel', 'accept'].includes(value),
        },
        updatedBy: {
            type: String,
            default: '',
        },
        updatedAt: {
            type: [Number, null],
            default: null,
        },
        entityName: {
            type: String,
            default: 'this entity',
        },
        entityType: {
            type: String,
            default: 'entity',
        },
        allowNote: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['confirm', 'cancel'],
    data () {
        return {
            note: '',
        };
    },
    computed: {
        entityHasI18nSupport () {
            // Any future entities that adopt verification should be i18nized from the start
            // else they should get the i18nized generic entity text
            return ['segment'].includes(this.entityType);
        },
        verificationModalBodyI18nKey () {
            if (!this.entityHasI18nSupport) {
                return {
                    verify: 'verificationModal.entity.verify.mainMessage',
                    accept: 'verificationModal.entity.verify.mainMessage',
                    unverify: 'verificationModal.entity.unverify.mainMessage',
                    request: 'verificationModal.entity.request.mainMessage',
                    rerequest: 'verificationModal.entity.rerequest.mainMessage',
                    reject: 'verificationModal.entity.reject',
                    cancel: 'verificationModal.entity.cancel.mainMessage',
                }[this.action];
            }

            return {
                verify: `verificationModal.${this.entityType}.verify.mainMessage`,
                accept: `verificationModal.${this.entityType}.verify.mainMessage`,
                unverify: `verificationModal.${this.entityType}.unverify.mainMessage`,
                request: `verificationModal.${this.entityType}.request.mainMessage`,
                rerequest: `verificationModal.${this.entityType}.rerequest.mainMessage`,
                reject: `verificationModal.${this.entityType}.reject`,
                cancel: `verificationModal.${this.entityType}.cancel.mainMessage`,
            }[this.action];
        },
        verificationModalTitleI18nKey () {
            if (!this.entityHasI18nSupport) {
                return {
                    verify: 'verificationModal.entity.verifyTitle',
                    accept: 'verificationModal.entity.verifyTitle',
                    unverify: 'verificationModal.entity.unverifyTitle',
                    request: 'verificationModal.Request verification?',
                    rerequest: 'verificationModal.Request verification again?',
                    reject: 'verificationModal.Reject request?',
                    cancel: 'verificationModal.Cancel request?',
                }[this.action];
            }

            return {
                verify: `verificationModal.${this.entityType}.verifyTitle`,
                accept: `verificationModal.${this.entityType}.verifyTitle`,
                unverify: `verificationModal.${this.entityType}.unverifyTitle`,
                request: 'verificationModal.Request verification?',
                rerequest: 'verificationModal.Request verification again?',
                reject: 'verificationModal.Reject request?',
                cancel: 'verificationModal.Cancel request?',
            }[this.action];
        },
        confirmButtonConfig () {
            return {
                verify: {
                    'label': this.$t('verificationModal.Verify'),
                    'type': 'primary',
                    'prefixIcon': 'shield-check',
                    'iconSize': 16,
                    'pendo-feature': `verify-${this.entityType}-button`,
                },
                accept: {
                    'label': this.$t('verificationModal.Verify'),
                    'type': 'primary',
                    'prefixIcon': 'shield-check',
                    'iconSize': 16,
                    'pendo-feature': `verify-${this.entityType}-button`,
                },
                unverify: {
                    label: this.$t('verificationModal.Unverify'),
                    type: 'danger',
                    prefixIcon: 'shield-x',
                    iconSize: 16,
                },
                request: {
                    'label': this.$t('verificationModal.Send request'),
                    'type': 'primary',
                    'disabled': this.note.length > 280,
                    'pendo-feature': `request-${this.entityType}-verification-button`,
                },
                rerequest: {
                    'label': this.$t('verificationModal.Request verification'),
                    'type': 'primary',
                    'disabled': this.note.length > 280,
                    'pendo-feature': `request-${this.entityType}-verification-button`,
                },
                reject: {
                    'label': this.$t('verificationModal.Reject'),
                    'type': 'danger',
                    'pendo-feature': `reject-${this.entityType}-button`,
                },
                cancel: { label: this.$t('verificationModal.Cancel request'), type: 'danger' },
            }[this.action];
        },
        updatedAtString () {
            return new Intl.DateTimeFormat(i18n_locale?.value, { dateStyle: 'long', timeStyle: 'short' }).format(
                this.updatedAt,
            );
        },
    },
    methods: {
        onConfirm () {
            this.$emit('confirm', this.note);
        },
        onCancel () {
            this.$emit('cancel');
        },
        onClosed () {
            this.note = '';
        },
    },
};
</script>

<style scoped lang="less">
:deep(.pendo-modal__body) {
    overflow-wrap: break-word;
}

.note-label {
    display: block;
    margin-bottom: 4px;
}

:deep(.textarea-input) {
    border: 1px solid var(--palette-gray-40);

    &:hover {
        border-color: var(--palette-gray-100);
    }

    &:focus-within {
        border-color: var(--palette-gray-100);
        outline: 2px solid var(--palette-gray-100);
    }
}
</style>
